import React, { useEffect, useRef } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import SVRBadge from 'components/SVRBadge';
import SVRComment from 'components/SVRComment';
// utils
import { animateScroll } from 'react-scroll';
import classnames from 'classnames/bind';
// styles
import styles from './QuestionViewRow.module.scss';

const cn = classnames.bind(styles);

const labelKeys = {
  fail: 'general.fail',
  pass: 'general.pass',
  not_available: 'general.notAvail',
};

const QuestionViewRow = ({ question }, { router: { location: { query: { mentionCommentId } } } }) => {
  const mentionedCommentRef = useRef(null);

  useEffect(() => {
    // TODO: make a research about scroller.scrollTo(elem id)
    if (mentionedCommentRef?.current) {
      const { y, height } = mentionedCommentRef.current.getBoundingClientRect();
      animateScroll.scrollTo(y - height, { containerId: 'svr-content-wrapper' });
    }
  }, [mentionedCommentRef]);

  const answerValue = question?.answer?.value;
  const isCorrected = !!question?.answer?.is_corrected;
  const isNotCorrected = !!question?.answer?.not_corrected;
  const isCarriedForward = !!question?.answer?.carried_forward;

  const carryForwardTooltip = isCarriedForward ? <FormattedMessage id="general.failedOnPreviousReport" /> : null;

  // One badge at a time: the decision (corrected / not corrected) supersedes the carried-forward mark
  const renderQuestionBadge = () => {
    if (isCorrected) {
      return (
        <SVRBadge color="green" tooltip={carryForwardTooltip}>
          <FormattedMessage id="general.corrected" />
        </SVRBadge>
      );
    }
    if (isNotCorrected) {
      return (
        <SVRBadge color="red" tooltip={carryForwardTooltip}>
          <FormattedMessage id="general.notCorrected" />
        </SVRBadge>
      );
    }
    if (isCarriedForward && answerValue === 'fail') {
      return (
        <SVRBadge color="red">
          <FormattedMessage id="general.failedOnPreviousReport" />
        </SVRBadge>
      );
    }
    return null;
  };

  const questionBadge = renderQuestionBadge();

  return (
    <div id={`svr-question-${question.id}`} className={cn('question-row', { flagged: question.answer?.is_flagged })}>
      <div className={cn('header')}>
        {/* info */}
        <div className={cn('info-column')}>
          <div className={cn('question-info')}>
            <div className={cn('icon', question.open_answer && answerValue ? 'fill_blank' : answerValue)}>
              {answerValue === 'fail' && !question.open_answer ?
                <i className="fa fa-times" /> :
                <i className="fa fa-check" />
              }
            </div>
            <span className={cn('question')}>
              {question.label}
            </span>
          </div>
          {questionBadge && <div className={cn('badge-row')}>{questionBadge}</div>}
        </div>
        {!question.open_answer && (
          <div className={cn('status', answerValue)}>
            {answerValue === 'pass' && <i className="fa fa-check mr-10" />}
            {answerValue === 'fail' && <i className="fa fa-times mr-10" />}
            <FormattedMessage id={labelKeys[answerValue]} />
          </div>
        )}
        {question.open_answer && (
          <div className={cn('status', 'fill_blank')}>
            <FormattedMessage id="general.fillBlank" />
          </div>
        )}
      </div>
      {question.open_answer && answerValue && (
        <div className={cn('open-answer-value')}>
          {answerValue}
        </div>
      )}
      {!!question?.answer?.comments?.length && (
      <div className={cn('comments')}>
        {question?.answer?.comments?.map((comment, idx) => (
          <div
            className={cn('comment-wrapper', { isMention: +mentionCommentId === comment.id })}
            ref={+mentionCommentId === comment.id ? mentionedCommentRef : undefined}
            key={idx}
          >
            <SVRComment className={idx === 0 ? 'pt-25' : ''} comment={comment}  />
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

QuestionViewRow.contextTypes = {
  router: T.object.isRequired
};

QuestionViewRow.propTypes = {
  question: T.object.isRequired
};

export default QuestionViewRow;
